import { View } from "react-native"
import { TRPCClientError } from "@trpc/react-query"
import { Alert } from "./ui/confirm"
import { useForm, Controller } from "react-hook-form"
import { router } from "expo-router"
import { zodResolver } from "@hookform/resolvers/zod"

import { createInventoryCollectionInput } from "@white-labels/input"
import { sharedStyles, trpc } from "@src/utils"
import { ar } from "i18n/ar"

import { Button, Text, TextInput } from "./core"

export function InventoryCollectionForm({ id }: { id?: number }) {
  const isEditing = !!id
  const utils = trpc.useUtils()
  const collection = trpc.inventoryCollections.get.useQuery({ id: id as number }, { enabled: isEditing })
  const createInventoryCollection = trpc.inventoryCollections.create.useMutation()
  const editInventoryCollection = trpc.inventoryCollections.edit.useMutation()

  const { control, handleSubmit, formState } = useForm({
    resolver: zodResolver(createInventoryCollectionInput),
    values: {
      name: collection.data?.name ?? "",
    },
  })

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      if (isEditing) {
        await editInventoryCollection.mutateAsync({ id: id as number, ...data })
        utils.inventoryCollections.listProducts.invalidate({ id: id as number })
      } else {
        await createInventoryCollection.mutateAsync(data)
      }

      utils.inventoryCollections.list.invalidate()
      router.back()
    } catch (error) {
      Alert.alert(ar.somethingWentWrong, error instanceof TRPCClientError ? error.message : ar.somethingWentWrong)
    }
  })

  const { errors, isValid } = formState
  const loading = createInventoryCollection.status === "pending" || editInventoryCollection.status === "pending"
  const disabled = !isValid || loading

  return (
    <View style={sharedStyles.contentContainer} className="mt-2">
      <Text className="text-lg font-bold">{ar.name}</Text>
      <Controller control={control} name="name" render={({ field }) => <TextInput value={field.value} onChangeText={field.onChange} autoFocus={!isEditing} />} />
      {errors.name && <Text className="text-red-500 mb-2">{errors.name.message}</Text>}

      <Button loading={loading} disabled={disabled} className="mt-4 self-end px-8 disabled:bg-gray-400" onPress={handleFormSubmit}>
        {isEditing ? ar.update : ar.save}
      </Button>
    </View>
  )
}
